import { useIntervalEffect, useSyncedRef, useThrottledCallback } from "@react-hookz/web";
import type {
  EmbeddedSpeechifyExperience,
  PlayerState,
  SpeechifyExperienceEvent,
} from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import { useSetAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";

import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { onlyOnePlayingPlayerAtom } from "~/lib/only-one-playing-player";

export const useBuildSpeechifyEventHandler = (
  articleSpeechifyPlayer: OrUndefined<EmbeddedSpeechifyExperience>,
) => {
  const dispatch = useSetAtom(onlyOnePlayingPlayerAtom);

  useSendThreeSecondTracking(articleSpeechifyPlayer);
  const sendProgressTrackingEvent = useThrottledCallback(
    ({ label }: { label: string }) => {
      sendGATracking({
        action: "TTS/Progress",
        category: "TTS",
        label,
      });
    },
    [sendGATracking],
    1000,
    true,
  );

  const speechifyEventHandler = useCallback(
    (event: SpeechifyExperienceEvent, playerState: PlayerState | undefined) => {
      switch (event.type) {
        case "voice-changed":
          sendGATracking({
            action: "TTS/voice changed",
            category: "TTS",
            label: playerState?.voiceDisplayName ?? "",
          });
          break;
        case "speed-changed":
          sendGATracking({
            action: "TTS/speed changed",
            category: "TTS",
            label: `${playerState?.speedWPM ?? ""}`,
          });
          break;
        case "pause":
          dispatch({
            type: "pauseArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Pause",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "play":
          dispatch({
            type: "playArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Play",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "progress":
          const percentageElapsed = Math.round((playerState?.progressFraction ?? 0) * 100);
          if (percentageElapsed > 0 && percentageElapsed % 10 === 0) {
            sendProgressTrackingEvent({
              label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
            });
          }
          break;
        case "progress-finished":
          sendGATracking({
            action: "TTS/Progress Finished",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
        case "resume":
          dispatch({
            type: "playArticleSpeechifyPlayer",
          });
          sendGATracking({
            action: "TTS/Resume",
            category: "TTS",
            label: `${playerState?.progressFraction} - ${playerState?.audioLengthSeconds}`,
          });
          break;
      }
    },
    [dispatch, sendProgressTrackingEvent],
  );

  return {
    speechifyEventHandler,
  };
};

export const useSendThreeSecondTracking = (
  articleSpeechifyPlayer: OrUndefined<EmbeddedSpeechifyExperience>,
) => {
  const [hasTriggeredThreeSecond, setHasTriggeredThreeSecond] = useState(false);
  useEffect(() => {
    if (!articleSpeechifyPlayer) return;
    setHasTriggeredThreeSecond(false);
  }, [articleSpeechifyPlayer]);

  useIntervalEffect(
    async () => {
      if (!articleSpeechifyPlayer) return;
      const { currentMs = 0 } = (await articleSpeechifyPlayer.getEstimatedTimeInfo()) ?? {};
      if (currentMs / 1000 > 3) {
        sendGATracking({
          action: `TTS/Speech progress 3s`,
          category: "TTS",
        });
        setHasTriggeredThreeSecond(true);
      }
    },
    hasTriggeredThreeSecond ? undefined : 500,
  );
};

export const useArticleSpeechUtils = () => {
  const { articleSpeechifyPlayerConfig } = useArticleSpeechContext();
  const latestArticleSpeechifyPlayerConfig = useSyncedRef(articleSpeechifyPlayerConfig);

  const checkIsCurrentArticlePlaying = useCallback(
    (articleElementReference: HTMLElement | null | undefined) => {
      if (
        !latestArticleSpeechifyPlayerConfig.current ||
        !latestArticleSpeechifyPlayerConfig.current.rootElement
      )
        return false;
      return latestArticleSpeechifyPlayerConfig.current.rootElement === articleElementReference;
    },
    [latestArticleSpeechifyPlayerConfig],
  );

  return {
    checkIsCurrentArticlePlaying,
  };
};
